import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Typography, Form, Input, Button, Space, Row, Col, Select, Modal, message } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useDatateamReprocess } from "../../hooks/useDatateamReprocess";
import { useTheme } from "../../hooks/useTheme";
import { parseIds } from "../../utils/parseIds";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * Página para reprocessar IDs do DataTeam.
 *
 * Permite que o usuário reprocesse múltiplos demandIds do DataTeam.
 */
const DatateamReprocessContent: React.FC = () => {
  const { setDemandIds, setEnvironment, setIdArtifact } = useDatateamReprocess();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [form] = Form.useForm();
  const demandIdsRef = useRef<HTMLTextAreaElement>(null);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [showBeautifyButton, setShowBeautifyButton] = useState(false);

  /**
   * Valida se uma string é um UUID válido.
   */
  const isValidUUID = (str: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str.trim());
  };

  /**
   * Valida os IDs e retorna mensagem de erro detalhada.
   */
  const validateIds = (value: string): { valid: boolean; error?: string } => {
    if (!value || value.trim().length === 0) {
      return { valid: false, error: "Por favor, digite pelo menos um demandId" };
    }

    const trimmedInput = value.trim();

    // Tenta fazer parse como JSON primeiro
    try {
      const parsed = JSON.parse(trimmedInput);

      // Verifica se é um array
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
          return { valid: false, error: "O array JSON está vazio. Adicione pelo menos um ID" };
        }

        // Verifica se todos os itens são UUIDs válidos
        const invalidIndexes: number[] = [];
        parsed.forEach((item, index) => {
          const strItem = String(item).trim();
          if (strItem.length === 0) {
            invalidIndexes.push(index);
          } else if (!isValidUUID(strItem)) {
            invalidIndexes.push(index);
          }
        });

        if (invalidIndexes.length > 0) {
          return {
            valid: false,
            error: `IDs inválidos (não são UUIDs) nas posições ${invalidIndexes.map((i) => i + 1).join(", ")} do array JSON (índices: ${invalidIndexes.join(", ")})`,
          };
        }

        return { valid: true };
      } else {
        return { valid: false, error: "O JSON deve ser um array. Use formato: [\"id1\", \"id2\", ...]" };
      }
    } catch (e: any) {
      // Se não for JSON válido, verifica se é formato de múltiplas linhas
      const lines = trimmedInput.split("\n");
      const validLines = lines.filter((line) => line.trim().length > 0);
      
      if (validLines.length === 0) {
        return { valid: false, error: "Por favor, digite pelo menos um demandId válido" };
      }

      // Verifica se todos os IDs são UUIDs válidos
      const invalidLines: number[] = [];
      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        if (trimmedLine.length > 0 && !isValidUUID(trimmedLine)) {
          invalidLines.push(index + 1);
        }
      });

      if (invalidLines.length > 0) {
        return {
          valid: false,
          error: `IDs inválidos (não são UUIDs) nas linhas: ${invalidLines.join(", ")}. Use apenas UUIDs válidos`,
        };
      }

      return { valid: true };
    }
  };

  /**
   * Verifica se o conteúdo é JSON válido.
   */
  const isJsonValid = (value: string | undefined): boolean => {
    if (!value || !value.trim()) {
      return false;
    }

    try {
      const parsed = JSON.parse(value.trim());
      return Array.isArray(parsed);
    } catch {
      return false;
    }
  };

  /**
   * Formata o JSON no campo.
   */
  const beautifyJson = () => {
    try {
      const values = form.getFieldsValue();
      const jsonText = values.demandIds;

      if (jsonText) {
        const parsed = JSON.parse(jsonText.trim());
        if (Array.isArray(parsed)) {
          const formatted = JSON.stringify(parsed, null, 2);
          form.setFieldsValue({ demandIds: formatted });
          message.success("JSON formatado com sucesso!");
        } else {
          message.warning("O JSON deve ser um array");
        }
      } else {
        message.warning("Preencha com um JSON");
      }
    } catch (error: any) {
      message.error("JSON inválido. Verifique a sintaxe");
    }
  };

  /**
   * Handler para submeter o formulário.
   */
  const handleSubmit = async (values: { demandIds: string; environment: string }) => {
    const demandIdsText = values.demandIds || "";
    if (demandIdsText.trim()) {
      const idList = parseIds(demandIdsText);

      if (idList.length > 0 && values.environment) {
        setDemandIds(idList);
        setEnvironment(values.environment);
        setIdArtifact(0); // idArtifact sempre é 0 no supreq
        navigate("/datateam/reprocessar/monitoring");
      } else {
        setDemandIds([]);
      }
    }
  };

  /**
   * Exemplos de demandIds do DataTeam para referência.
   */
  const exampleIds = ["e8fac186-9236-4d4a-923f-5a1922806b66", "eaea1d33-912e-4bad-8c2c-fc73a515a72d"];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Title level={2} style={{ marginBottom: 0 }}>
              <SendOutlined /> Reprocessar DataTeam
            </Title>
            <Button type="primary" onClick={() => setShowExamplesModal(true)}>
              Ver exemplos
            </Button>
          </div>
          <Paragraph>
            Digite os demandIds do DataTeam que deseja reprocessar. Você pode usar dois formatos: um ID por linha ou um array JSON. Os IDs serão enviados para a fila de reprocessamento.
          </Paragraph>
          <Paragraph style={{ fontSize: "13px", color: theme === "dark" ? "#ffffff" : "rgba(0, 0, 0, 0.65)", marginTop: 4 }}>
            Os demandIds serão enviados para a fila de reprocessamento do DataTeam. Você poderá visualizar o status de cada ID após o envio. O idArtifact será sempre 0 no SupReq.
          </Paragraph>
        </div>

        <Modal
          title="Formatos Aceitos"
          open={showExamplesModal}
          onCancel={() => setShowExamplesModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowExamplesModal(false)}>
              Fechar
            </Button>,
          ]}
          width={1200}
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text strong>Formato 1 - Múltiplas linhas:</Text>
                <TextArea
                  value={exampleIds.join("\n")}
                  readOnly
                  rows={exampleIds.length}
                  style={{ fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                />
              </Space>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text strong>Formato 2 - Array JSON:</Text>
                <TextArea
                  value={JSON.stringify(exampleIds, null, 2)}
                  readOnly
                  rows={JSON.stringify(exampleIds, null, 2).split("\n").length}
                  style={{ fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                />
              </Space>
            </Col>
          </Row>
        </Modal>

        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="environment"
            label="Ambiente"
            initialValue="saj"
            rules={[{ required: true, message: "Por favor, selecione um ambiente" }]}
          >
            <Select placeholder="Selecione o ambiente">
              <Option value="projuris">Projuris</Option>
              <Option value="saj">Saj</Option>
              <Option value="dev">Dev</Option>
              <Option value="peticiona">Peticiona</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="demandIds"
            label="Demand IDs"
            validateStatus={validationError ? "error" : undefined}
            help={validationError}
            rules={[
              { required: true, message: "Por favor, digite pelo menos um demandId" },
              {
                validator: (_, value) => {
                  const validation = validateIds(value);
                  if (!validation.valid) {
                    return Promise.reject(new Error(validation.error));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              ref={demandIdsRef}
              rows={10}
              placeholder='Digite os demandIds (um por linha) ou um array JSON: ["id1", "id2", ...]'
              style={{ fontFamily: "monospace" }}
              onChange={(e) => {
                const value = e.target.value;
                setShowBeautifyButton(isJsonValid(value));
                
                // Validação em tempo real
                if (value.trim().length === 0) {
                  setValidationError(undefined);
                  return;
                }
                
                const validation = validateIds(value);
                if (!validation.valid) {
                  setValidationError(validation.error);
                } else {
                  setValidationError(undefined);
                }
              }}
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                {showBeautifyButton && (
                  <Button onClick={beautifyJson}>JSON Beautify</Button>
                )}
              </div>
              <Button type="primary" htmlType="submit" icon={<SendOutlined />} size="large">
                Reprocessar
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página DatateamReprocess.
 */
const DatateamReprocess: React.FC = () => {
  return (
    <DatateamReprocessContent />
  );
};

export default DatateamReprocess;

